class SearchNavigation {
  constructor() {
    // Enable debug mode via window.searchNavigationDebug or URL parameter
    this.debugMode =
      window.searchNavigationDebug ||
      new URLSearchParams(window.location.search).get("debug") === "search" ||
      false

    this.debug("🔍 SearchNavigation: Initializing", {
      debugMode: this.debugMode,
      pathname: window.location.pathname,
    })

    this.el = document.querySelector("[data-search]")
    if (!this.el) {
      this.debug(
        "❌ SearchNavigation: [data-search] element not found",
        null,
        true
      )
      return
    }

    this.searchBtn = this.el.querySelector("[data-search-btn]")
    this.searchInput = this.el.querySelector("[data-search-input]")
    this.searchForm = this.el.querySelector("form")
    this.searchField = this.el.querySelector('input[type="search"]')
    this.resetBtn = this.el.querySelector("[data-search-reset]")
    this.submitBtn = this.el.querySelector("[data-search-submit]")
    this.isProjectsPage = window.location.pathname.includes("/projects")
    this.navigationListenerSetup = false

    this.debug("✅ SearchNavigation: Elements found", {
      hasSearchBtn: !!this.searchBtn,
      hasSearchInput: !!this.searchInput,
      hasSearchForm: !!this.searchForm,
      hasSearchField: !!this.searchField,
      hasResetBtn: !!this.resetBtn,
      hasSubmitBtn: !!this.submitBtn,
      isProjectsPage: this.isProjectsPage,
    })

    this.init()
  }

  debug(message, data = null, important = false) {
    if (!this.debugMode && !important) return

    const prefix = important ? "🚨 [IMPORTANT]" : "🔍 [SearchNavigation]"
    if (data) {
      console.log(`${prefix} ${message}`, data)
    } else {
      console.log(`${prefix} ${message}`)
    }
  }

  init() {
    this.debug("🚀 SearchNavigation: init() called", {
      isProjectsPage: this.isProjectsPage,
    })

    this.bindEvents()
    if (this.isProjectsPage) {
      this.debug("📍 On projects page - initializing search")
      this.initializeSearch()
      // On projects page, hide submit button (instant search, no need to submit)
      if (this.submitBtn) {
        this.submitBtn.style.display = "none"
        this.debug("👁️ Submit button hidden (projects page - instant search)")
      }
    } else {
      this.debug("📍 Not on projects page - resetting to initial state")
      // If not on projects page but search is active, reset it
      this.resetToInitialState()
    }

    // Listen for navigation events to reset search when leaving projects page
    this.setupNavigationListener()
  }

  bindEvents() {
    this.searchBtn?.addEventListener("click", () => this.toggleSearch())
    this.searchForm?.addEventListener("submit", (e) => this.handleSearch(e))
    this.resetBtn?.addEventListener("click", () => this.resetSearch())
    this.searchField?.addEventListener("input", (e) => this.handleInput(e))
  }

  toggleSearch() {
    this.debug("🔄 toggleSearch() called", {
      isProjectsPage: this.isProjectsPage,
    })

    this.searchBtn.classList.add("hidden")
    this.searchInput.classList.remove("hidden")
    // Show submit button on other pages, hide on projects page (instant search)
    if (this.submitBtn) {
      const displayValue = this.isProjectsPage ? "none" : ""
      this.submitBtn.style.display = displayValue
      this.debug("👁️ Submit button visibility updated", {
        display: displayValue,
        reason: this.isProjectsPage
          ? "projects page (instant search)"
          : "other page (needs submit)",
      })
    }
    this.searchField.focus()
  }

  handleInput(e) {
    const value = e.target.value.trim()

    this.debug("⌨️ handleInput() called", {
      value: value,
      valueLength: value.length,
      isProjectsPage: this.isProjectsPage,
    })

    // Show/hide reset button based on input value
    if (this.resetBtn) {
      const shouldHide = !value
      this.resetBtn.classList.toggle("hidden", shouldHide)
      this.debug("👁️ Reset button visibility", {
        hidden: shouldHide,
        hasValue: !!value,
      })
    }

    // If on projects page, dispatch search event
    if (this.isProjectsPage) {
      this.dispatchSearchEvent(value)
    }
  }

  resetSearch() {
    this.debug("🔄 resetSearch() called", {
      isProjectsPage: this.isProjectsPage,
    })

    // Reset to initial state (hides search input, shows search button, etc.)
    this.resetToInitialState()

    // Dispatch empty search event to clear ProjectList filter if on projects page
    if (this.isProjectsPage) {
      this.dispatchSearchEvent("")
    }
  }

  handleSearch(e) {
    e.preventDefault()
    const searchValue = this.searchField.value.trim()

    this.debug("🔍 handleSearch() called", {
      searchValue: searchValue,
      isProjectsPage: this.isProjectsPage,
    })

    if (this.isProjectsPage) {
      // On projects page, dispatch search event
      this.debug("📍 On projects page - dispatching search event")
      this.dispatchSearchEvent(searchValue)
    } else {
      // Not on projects page, navigate using Highway (no hard refresh)
      const redirectUrl = `/projects${
        searchValue ? `?search=${encodeURIComponent(searchValue)}` : ""
      }`
      this.debug("🔄 Navigating to projects page using Highway", {
        url: redirectUrl,
        hasHighway: !!window.H,
      })

      // Use history.pushState to update URL without hard refresh
      // Then manually trigger Highway navigation
      history.pushState({}, "", redirectUrl)
      this.debug("✅ History state updated", { url: redirectUrl })

      // Manually trigger Highway navigation by dispatching popstate
      // Highway should handle this and load the new page content
      window.dispatchEvent(new PopStateEvent("popstate", { state: {} }))
      this.debug("✅ PopState event dispatched for Highway navigation")
    }
  }

  initializeSearch() {
    this.debug("🚀 initializeSearch() called")

    // Get search query from URL if present
    const urlParams = new URLSearchParams(window.location.search)
    const searchQuery = urlParams.get("search")

    this.debug("🔍 URL search query", {
      searchQuery: searchQuery,
      hasSearchQuery: !!searchQuery,
    })

    // On projects page, hide submit button (instant search, no need to submit)
    if (this.submitBtn) {
      this.submitBtn.style.display = "none"
      this.debug("👁️ Submit button hidden (projects page - instant search)")
    }

    if (searchQuery) {
      this.debug("✅ Search query found - showing search input", {
        query: searchQuery,
      })

      // Show search input and hide search button
      this.searchBtn.classList.add("hidden")
      this.searchInput.classList.remove("hidden")

      // Set the search input value
      if (this.searchField) {
        this.searchField.value = searchQuery
        if (this.resetBtn) {
          this.resetBtn.classList.remove("hidden")
        }
        // Dispatch initial search event
        this.dispatchSearchEvent(searchQuery)
      }
    } else {
      this.debug("ℹ️ No search query in URL")
    }
  }

  dispatchSearchEvent(searchValue) {
    const trimmedValue = searchValue.trim()

    this.debug("📡 dispatchSearchEvent() called", {
      searchValue: trimmedValue,
      valueLength: trimmedValue.length,
    })

    // Dispatch custom event for ProjectList component
    const event = new CustomEvent("projectListSearch", {
      detail: { searchValue: trimmedValue },
      bubbles: true,
    })
    window.dispatchEvent(event)

    this.debug("✅ Search event dispatched", {
      eventName: "projectListSearch",
      detail: event.detail,
    })
  }

  resetToInitialState() {
    this.debug("🔄 resetToInitialState() called", {
      isProjectsPage: this.isProjectsPage,
    })

    // Reset search field value
    if (this.searchField) {
      this.searchField.value = ""
    }

    // Hide search input and show search button
    if (this.searchBtn) {
      this.searchBtn.classList.remove("hidden")
    }
    if (this.searchInput) {
      this.searchInput.classList.add("hidden")
    }

    // Hide reset button
    if (this.resetBtn) {
      this.resetBtn.classList.add("hidden")
    }

    // Show submit button on other pages, hide on projects page (instant search)
    if (this.submitBtn) {
      const displayValue = this.isProjectsPage ? "none" : ""
      this.submitBtn.style.display = displayValue
      this.debug("👁️ Submit button visibility updated", {
        display: displayValue,
        reason: this.isProjectsPage
          ? "projects page (instant search)"
          : "other page (needs submit)",
      })
    }

    this.debug("✅ Reset to initial state complete")
  }

  setupNavigationListener() {
    // Only set up listeners once
    if (this.navigationListenerSetup) {
      this.debug("ℹ️ Navigation listeners already setup")
      return
    }
    this.navigationListenerSetup = true

    this.debug("🎧 Setting up navigation listeners", {
      hasHighway: !!window.H,
    })

    // Listen for Highway navigation events
    if (window.H) {
      window.H.on("NAVIGATE_OUT", ({ from }) => {
        this.debug("🚪 NAVIGATE_OUT event", {
          from: from?.view?.getAttribute?.("data-class-injection"),
        })

        // Check if 'view' has data-class-injection="template-list"
        if (
          from.view &&
          from.view.hasAttribute &&
          from.view.hasAttribute("data-class-injection") &&
          from.view.getAttribute("data-class-injection") === "template-list"
        ) {
          this.debug("📍 Leaving projects page - resetting to initial state")
          this.resetToInitialState()
        }
      })

      // Update submit button visibility when navigating to/from projects page
      window.H.on("NAVIGATE_IN", () => {
        this.debug("🚪 NAVIGATE_IN event")

        // Small delay to allow URL to update
        setTimeout(() => {
          const isProjectsPage = window.location.pathname.includes("/projects")
          this.debug("🔄 Navigation complete", {
            pathname: window.location.pathname,
            isProjectsPage: isProjectsPage,
            previousIsProjectsPage: this.isProjectsPage,
          })

          if (this.submitBtn) {
            const displayValue = isProjectsPage ? "none" : ""
            this.submitBtn.style.display = displayValue
            this.debug("👁️ Submit button visibility updated after navigation", {
              display: displayValue,
              reason: isProjectsPage
                ? "projects page (instant search)"
                : "other page (needs submit)",
            })
          }
          // Update isProjectsPage flag
          this.isProjectsPage = isProjectsPage
          // Re-initialize search if on projects page
          if (isProjectsPage) {
            this.initializeSearch()
          }
        }, 0)
      })
    } else {
      this.debug(
        "⚠️ Highway (window.H) not found - navigation listeners not set up",
        null,
        true
      )
    }

    // Also listen for popstate (browser back/forward)
    this.handlePopState = () => {
      this.debug("⬅️ popstate event (browser back/forward)")

      // Small delay to allow URL to update
      setTimeout(() => {
        const isProjectsPage = window.location.pathname.includes("/projects")
        this.debug("🔄 Popstate complete", {
          pathname: window.location.pathname,
          isProjectsPage: isProjectsPage,
          previousIsProjectsPage: this.isProjectsPage,
        })

        if (this.submitBtn) {
          const displayValue = isProjectsPage ? "none" : ""
          this.submitBtn.style.display = displayValue
          this.debug("👁️ Submit button visibility updated after popstate", {
            display: displayValue,
            reason: isProjectsPage
              ? "projects page (instant search)"
              : "other page (needs submit)",
          })
        }
        // Update isProjectsPage flag
        this.isProjectsPage = isProjectsPage
        if (!isProjectsPage) {
          this.resetToInitialState()
        } else {
          // Re-initialize search if on projects page
          this.initializeSearch()
        }
      }, 0)
    }
    window.addEventListener("popstate", this.handlePopState)

    this.debug("✅ Navigation listeners setup complete")
  }
}

export default SearchNavigation
